import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
} from "@babackapp/ui/components/select";
import { rolesById } from "@babackapp/private-api-client/rights/roles-by-id";
import { useMemo } from "react";
import useTranslation from "@babackapp/translations/use-translation";
import { ShopMemberDto } from "@babackapp/private-api-client";

type RoleSelectProps = {
  value?: ShopMemberDto["roleId"];
  onChange: (value: ShopMemberDto["roleId"]) => void;
  onRemove?: () => void;
};

export function RoleSelect({ value, onChange, onRemove }: RoleSelectProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const rolesOptions = useMemo(() => {
    if (!shop) return [];
    return Object.values(rolesById).filter(
      /**
       * The Owner role is only assigned automatically
       * and cannot be selected here
       */
      (role) =>
        role.id !== "owner" &&
        /**
         * It is possible to assign the logistician role
         * only if the logistician workflow is activated.
         * If not, the role is removed from the list
         */
        (shop.workflowLogistician.activated || role.id !== "logistician"),
    );
  }, [shop]);

  const roleValue = rolesById[value ?? "OWNER"];

  const onValueChange = (value: ShopMemberDto["roleId"] | "remove") => {
    if (value === "remove") return onRemove?.();
    onChange(value);
  };

  return (
    <div className="inline">
      <Select value="owner" onValueChange={onValueChange}>
        <SelectTrigger className="h-10">
          {value ? (
            <span className="text-left">{t(`role.${roleValue.key}.name`)}</span>
          ) : (
            "Select a value"
          )}
        </SelectTrigger>
        <SelectContent className="max-w-80" viewportClassName="max-w-80">
          {rolesOptions.map((role) => (
            <SelectItem key={role.id} value={role.value}>
              <div className="flex flex-col gap-2 p-2 py-3">
                <span className="text-left font-semibold">
                  {t(`role.${role.key}.name`)}
                </span>
                <span className="text-muted-foreground text-xs font-medium">
                  {t(`role.${role.key}.description`)}
                </span>
              </div>
            </SelectItem>
          ))}
          <SelectSeparator />
          {onRemove && (
            <SelectItem value="remove">
              <div className="flex flex-col gap-2 p-2 py-3">
                <span className="text-left font-semibold text-destructive">
                  {t("settings.team.delete")}
                </span>
                <span className="text-muted-foreground text-xs font-medium">
                  {t("settings.team.deleteDescription")}
                </span>
              </div>
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
